// --- START OF FILE ModelViewer.react.js ---

import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

const ModelViewer = (props) => {
  const modelViewerRef = useRef(null);

  // Effect to load the model-viewer script and set up initial listeners
  useEffect(() => {
    const existingScript = document.querySelector('script[src="https://ajax.googleapis.com/ajax/libs/model-viewer/3.5.0/model-viewer.min.js"]');
    let script;

    const initializeModelViewer = () => {
      customElements.whenDefined('model-viewer').then(() => {
         if (modelViewerRef.current) {
            setupModelViewerListeners(); // Run combined setup
         }
      });
    };

    if (!existingScript) {
        script = document.createElement('script');
        script.src = "https://ajax.googleapis.com/ajax/libs/model-viewer/3.5.0/model-viewer.min.js";
        script.type = "module";
        script.addEventListener('load', initializeModelViewer);
        document.body.appendChild(script);
    } else {
        if (customElements.get('model-viewer')) {
            initializeModelViewer();
        } else {
            customElements.whenDefined('model-viewer').then(() => {
                if (modelViewerRef.current) {
                   initializeModelViewer();
                }
            });
        }
    }

    return () => {
      if (script && script.parentElement === document.body) {
        document.body.removeChild(script);
      }
      const modelViewerElement = modelViewerRef.current;
       if (modelViewerElement) {
           // Clean up listeners if needed - requires stable function references
           // For simplicity here, we rely on component unmount/hot reload cleanup
           // In a larger app, more robust cleanup might be needed
       }
    };
  }, []); // Empty dependency array: run only once on mount

  // Combined setup function for listeners
  const setupModelViewerListeners = () => {
        setupHotspots(); // Setup/update hotspot listeners
        setupAR();       // Setup/update AR listeners
   };


  // Setup Hotspots - including CLICK logic for camera views
  const setupHotspots = () => {
    const modelViewer = modelViewerRef.current;
    if (!modelViewer) return;

    // --- Hotspot Click Logic for Camera Views ---
    // This function will be attached to hotspot clicks
    const handleAnnotationClick = (event) => {
      const annotation = event.currentTarget; // The clicked hotspot div/button
      const modelViewerElement = modelViewerRef.current; // Get the <model-viewer> element
      if (!modelViewerElement) return;

      const dataset = annotation.dataset; // Access data-* attributes

      // Check if data-target and data-orbit exist (specific to camera view hotspots)
      if (dataset.target && dataset.orbit) {
          // console.log("Updating camera:", dataset.target, dataset.orbit); // Debug
          modelViewerElement.cameraTarget = dataset.target;
          modelViewerElement.cameraOrbit = dataset.orbit;
          // Optionally set FoV if needed, or make it a data attribute too
          // modelViewerElement.fieldOfView = '45deg'; // Set FoV as in example
          // If FoV control is desired per-hotspot, add data-fov attribute
          if (dataset.fov) {
             modelViewerElement.fieldOfView = dataset.fov;
          } else {
             // Default FoV for camera views if not specified
             modelViewerElement.fieldOfView = '45deg';
          }
      }
      // Add other click logic here if needed for different types of hotspots
    };

    // Query *all* elements intended as hotspots within this instance
    const hotspotElements = modelViewer.querySelectorAll('.hotspot'); // Use the base class

    // Update listeners: Remove potentially old, add new
    hotspotElements.forEach((hotspot) => {
      // It's safer to remove the specific listener reference if possible.
      // If the function is redefined on each render, direct removal fails.
      // For this pattern, simply adding it might be okay if duplicates aren't harmful,
      // but re-attaching ensures the LATEST `handleAnnotationClick` is used.
      // Let's try removing first for hygiene, assuming stable enough reference or effect re-run.
      hotspot.removeEventListener('click', handleAnnotationClick); // Attempt removal
      hotspot.addEventListener('click', handleAnnotationClick);   // Add listener
    });
  };

  // Setup AR Listeners and Button Logic - unchanged from previous
  const setupAR = () => {
    const modelViewer = modelViewerRef.current;
    if (!modelViewer) return;

    const arButton = modelViewer.querySelector('#ar-button');
    const arPrompt = modelViewer.querySelector('#ar-prompt');
    const arFailure = modelViewer.querySelector('#ar-failure');

    const handleARButtonClick = () => { if (modelViewer?.canActivateAR) { modelViewer.activateAR(); } };
    const handleArStatus = (event) => { if (arPrompt) { arPrompt.style.display = event.detail.status === 'session-started' ? 'block' : 'none'; } };
    const handleArTracking = (event) => { if (arFailure) { arFailure.style.display = event.detail.status === 'not-tracking' ? 'block' : 'none'; } };

    if (arButton) {
        arButton.removeEventListener('click', handleARButtonClick);
        arButton.addEventListener('click', handleARButtonClick);
    }
    modelViewer.removeEventListener('ar-status', handleArStatus);
    modelViewer.addEventListener('ar-status', handleArStatus);
    modelViewer.removeEventListener('ar-tracking', handleArTracking);
    modelViewer.addEventListener('ar-tracking', handleArTracking);
  };

  // Effect to re-run setupHotspots when the 'hotspots' prop changes
  useEffect(() => {
    // console.log("Hotspots prop changed, re-running setupHotspots"); // Debug
    customElements.whenDefined('model-viewer').then(() => {
       if (modelViewerRef.current) {
            setupHotspots(); // Re-attach click listeners to potentially new hotspot elements
       }
    });
  }, [props.hotspots]); // Dependency on the hotspots prop array


  // Destructure props passed from Dash
  const {
    id, src, alt, cameraControls, touchAction, cameraOrbit, cameraTarget,
    fieldOfView, minFieldOfView, maxFieldOfView, interpolationDecay,
    minCameraOrbit, maxCameraOrbit,
    poster, ar, hotspots, // hotspots is the array from Python
    arButtonText, style, customArPrompt, customArFailure,
    toneMapping, arModes, shadowIntensity, arScale,
    variantName
  } = props;

  // Convert boolean props for attributes
  const cameraControlsAttr = cameraControls ? '' : null;
  const arAttr = ar ? '' : null;

  return (
    <model-viewer
      ref={modelViewerRef}
      id={id}
      src={src}
      alt={alt}
      camera-controls={cameraControlsAttr}
      touch-action={touchAction}
      camera-orbit={cameraOrbit}
      camera-target={cameraTarget}
      field-of-view={fieldOfView}
      min-field-of-view={minFieldOfView}
      max-field-of-view={maxFieldOfView}
      interpolation-decay={interpolationDecay}
      min-camera-orbit={minCameraOrbit}
      max-camera-orbit={maxCameraOrbit} // Pass max orbit if needed
      poster={poster}
      ar={arAttr}
      style={style}
      tone-mapping={toneMapping}
      ar-modes={arModes}
      shadow-intensity={shadowIntensity}
      ar-scale={arScale}
      variant-name={variantName === 'default' ? null : variantName}
    >
      {/* Render hotspot divs based on the hotspots prop array */}
      {hotspots && hotspots.map((hotspot, index) => (
        <div
          key={hotspot.slot || `hotspot-${index}`}
           // Apply base 'hotspot' class + specific class from prop (e.g., 'view-button')
          className={`hotspot ${hotspot.children_classname || ''}`.trim()}
          slot={hotspot.slot || `hotspot-${index + 1}`}
          data-position={hotspot.position}
          data-normal={hotspot.normal}
          // Add camera control data attributes IF THEY EXIST in the hotspot definition
          {...(hotspot.orbit && { 'data-orbit': hotspot.orbit })}
          {...(hotspot.target && { 'data-target': hotspot.target })}
          {...(hotspot.fov && { 'data-fov': hotspot.fov })} // Support for per-hotspot FoV if needed
        >
          {/* Text content (button label) */}
          {hotspot.text}
        </div>
      ))}

      {/* AR Button Slot */}
      <div id="ar-button" slot="ar-button">
        {arButtonText || "View in your space"}
      </div>

      {/* AR Prompt Slot */}
      <div id="ar-prompt" style={{display: 'none'}}>
        {customArPrompt || <img src="https://modelviewer.dev/assets/hand.png" alt="AR Prompt"/>}
      </div>

      {/* AR Failure Slot */}
      <div id="ar-failure" style={{display: 'none'}}>
        {customArFailure || "AR is not tracking!"}
      </div>
    </model-viewer>
  );
};

// --- Default Props ---
ModelViewer.defaultProps = {
  cameraControls: true,
  touchAction: "pan-y",
  ar: true,
  hotspots: [],
  style: {},
  toneMapping: 'neutral',
  arModes: "webxr scene-viewer quick-look",
  shadowIntensity: 0,
  arScale: 'auto',
  interpolationDecay: 50,
  fieldOfView: 'auto',
  minFieldOfView: '25deg',
  maxFieldOfView: 'auto',
  maxCameraOrbit: 'auto auto auto', // Added default
  minCameraOrbit: 'auto auto auto',
  variantName: null,
  arButtonText: 'View in your space',
  customArPrompt: null,
  customArFailure: null,
  cameraOrbit: undefined,
  cameraTarget: undefined,
  poster: undefined,
};

// --- Prop Types ---
ModelViewer.propTypes = {
  /** Component ID */
  id: PropTypes.string.isRequired,
  /** Model source URL (.glb or .gltf) */
  src: PropTypes.string.isRequired,
  /** Alt text for accessibility */
  alt: PropTypes.string.isRequired,
  /** Enable camera controls */
  cameraControls: PropTypes.bool,
  /** Touch action behavior */
  touchAction: PropTypes.oneOf(['pan-y', 'pan-x', 'none']),
  /** Initial camera orbital position ($theta $phi $radius) */
  cameraOrbit: PropTypes.string,
  /** Initial camera target point ($X $Y $Z) */
  cameraTarget: PropTypes.string,
  /** Camera field of view */
  fieldOfView: PropTypes.string,
  /** Minimum camera field of view */
  minFieldOfView: PropTypes.string,
  /** Maximum camera field of view */
  maxFieldOfView: PropTypes.string,
  /** Camera interpolation decay rate */
  interpolationDecay: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /** Minimum camera orbit bounds */
  minCameraOrbit: PropTypes.string,
  /** Maximum camera orbit bounds */
  maxCameraOrbit: PropTypes.string, // Added prop type
  /** Poster image URL */
  poster: PropTypes.string,
  /** Enable AR features */
  ar: PropTypes.bool,

  /** Array of hotspot objects passed from the server */
  hotspots: PropTypes.arrayOf(PropTypes.shape({
    slot: PropTypes.string,
    position: PropTypes.string,
    normal: PropTypes.string,
    orbit: PropTypes.string,    // Camera orbit for click
    target: PropTypes.string,   // Camera target for click
    fov: PropTypes.string,      // Optional: Field of view for click
    text: PropTypes.string,
    children_classname: PropTypes.string, // CSS class name (e.g., 'view-button')
  })),

  /** Text for the default AR button */
  arButtonText: PropTypes.string,
  /** Custom React element for the AR prompt */
  customArPrompt: PropTypes.node,
  /** Custom React element for the AR failure message */
  customArFailure: PropTypes.node,
  /** CSS Style object */
  style: PropTypes.object,

  // --- Rendering & AR Prop Types ---
  toneMapping: PropTypes.oneOf(['neutral', 'aces', 'agx', 'reinhard', 'cineon', 'linear', 'none']),
  arModes: PropTypes.string,
  shadowIntensity: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  arScale: PropTypes.oneOf(['auto', 'fixed']),
  variantName: PropTypes.string,
};

export default ModelViewer;
// --- END OF FILE ModelViewer.react.js ---
